import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useOutletContext } from 'react-router'
import styled from 'styled-components'
import { EmojiEvents } from '@material-ui/icons'

import { COLORS, Devices, Spacings, Text } from '@styles'
import { Table } from '@baseV2'
import { showLoader } from '@data/state/action'

import { getLeaderboardConfig, ALL_FETCH_KEYS } from './leaderboardConfig'

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: ${Spacings.SPACING_2B};
	font-family: 'Inter', sans-serif;

	* {
		font-family: 'Inter', sans-serif;
	}

	@media ${Devices.tablet} {
		padding: 0;
	}
`

const Section = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: ${Spacings.SPACING_3B};

	@media ${Devices.tablet} {
		grid-template-columns: repeat(3, 1fr);
	}
`

const Card = styled.div`
	display: flex;
	flex-direction: column;
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.lg};
	overflow: hidden;
	background: ${({ theme }) => theme.colors.surface.card};
`

const CardHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
	background: ${({ theme, accent }) => theme.colors.status[accent].bg};

	svg {
		font-size: ${Text.X_LARGE};
		color: ${({ theme }) => theme.colors.text.secondary};
	}
`

const CardTitle = styled.span`
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.text.primary};
	text-transform: uppercase;
	letter-spacing: 0.04em;
`

const HeaderEnd = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
`

const CardTotal = styled.span`
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.text.primary};
`

const ScrollableTable = styled(Table)`
	height: 360px;
	overflow-y: auto;
	border: none;
	border-radius: 0;

	-ms-overflow-style: none;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}

	thead {
		position: sticky;
		top: 0;
		z-index: 1;
	}
`

// Gold, silver, bronze. `accent` picks the pill background from the status palette;
// `icon` is the trophy colour. Bronze takes a light orange-yellow from the yellow ramp.
const MEDALS = [
	{ accent: 'warning', icon: (theme) => theme.colors.status.warning.icon },
	{ accent: 'neutral', icon: (theme) => theme.colors.status.neutral.icon },
	{ accent: 'orange', icon: () => COLORS.yellow[200] },
]

const Badge = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: ${Spacings.SPACING_6B};
	height: ${Spacings.SPACING_5B};
	padding: 0 ${Spacings.SPACING_1B};
	border-radius: ${({ theme }) => theme.radius.full};
	font-size: ${Text.EXTRA_SMALL};
	font-weight: ${Text.BOLD_600};
	background: ${({ medal, theme }) => (medal ? theme.colors.status[medal.accent].bg : 'transparent')};
	color: ${({ medal, theme }) => (medal ? medal.icon(theme) : theme.colors.text.secondary)};

	svg {
		font-size: ${Text.M_LARGE};
	}
`

const RankBadge = (rank) => {
	const medal = MEDALS[rank - 1]
	if (medal) {
		return (
			<Badge medal={medal}>
				<EmojiEvents />
			</Badge>
		)
	}
	return <Badge>{rank}</Badge>
}

const BASE_WIDTHS = { rank: 18, name: 52, value: 30 }
const EXTRA_WIDTHS = { rank: 21, name: 43, extra: 18, value: 18 } // Figma node 10765:88320

const buildColumns = (card) => {
	const extraColumns = card.extraColumns ?? []
	const widths = extraColumns.length ? EXTRA_WIDTHS : BASE_WIDTHS
	return [
		{ label: '#', cellWidth: widths.rank, key: (row) => RankBadge(row.rank) },
		{ label: 'Name', cellWidth: widths.name, key: 'name' },
		...extraColumns.map((col, i) => ({
			label: col.label,
			cellWidth: widths.extra,
			align: 'right',
			key: `extra_${i}`,
		})),
		{ label: card.valueLabel, cellWidth: widths.value, align: 'right', key: 'value' },
	]
}

const NAME_KEYS = ['name', 'userName', 'user_name', 'fullName', 'full_name', 'salesPerson', 'employeeName', 'user']

const toBaseRevenue = (value, unit) => {
	const num = Number(value) || 0
	const normalized = String(unit ?? '')
		.trim()
		.toLowerCase()
	if (normalized === 'cr') return num * 1e7
	if (normalized === 'lakhs') return num * 1e5
	return num
}

const firstDefined = (obj, keys) => {
	if (!obj || typeof obj !== 'object') return undefined
	return keys.map((key) => obj[key]).find((val) => val !== undefined && val !== null)
}

const normalizeRows = (response, card = {}) =>
	(Array.isArray(response) ? response : []).map((item, index) => {
		const rawName = firstDefined(item, NAME_KEYS)
		const name = rawName && typeof rawName === 'object' ? firstDefined(rawName, NAME_KEYS) ?? '—' : rawName ?? '—'
		const rawValue = item?.[card.valueKey]
		const extras = (card.extraColumns ?? []).reduce((acc, col, i) => ({ ...acc, [`extra_${i}`]: col.formatValue(item) }), {})
		return {
			rank: item?.rank ?? index + 1,
			name,
			...extras,
			value: card.formatValue ? card.formatValue(item, rawValue) : rawValue ?? '',
		}
	})

const StateMessage = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 360px;
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
`

const Leaderboard = () => {
	const dispatch = useDispatch()
	const user = useSelector((state) => state.getIn(['user', 'user']))
	const userId = user?.user_id

	const [, , filters] = useOutletContext() || []
	const { type, millis, subRole, platforms } = filters || {}

	const activeConfig = useMemo(() => getLeaderboardConfig(type), [type])

	const subRoleParam = subRole || undefined
	const platformsParam = useMemo(() => (platforms?.length ? platforms.map((item) => item.key).join(',') : undefined), [platforms])

	const [responses, setResponses] = useState(() => ALL_FETCH_KEYS.reduce((acc, key) => ({ ...acc, [key]: null }), {}))
	const [loadingMap, setLoadingMap] = useState(() => ALL_FETCH_KEYS.reduce((acc, key) => ({ ...acc, [key]: true }), {}))

	const runFetch = useCallback(async (dataKey, promise, isMounted) => {
		setLoadingMap((m) => ({ ...m, [dataKey]: true }))
		try {
			const res = await promise
			if (isMounted()) setResponses((r) => ({ ...r, [dataKey]: res }))
		} catch {
			if (isMounted()) setResponses((r) => ({ ...r, [dataKey]: null }))
		} finally {
			if (isMounted()) setLoadingMap((m) => ({ ...m, [dataKey]: false }))
		}
	}, [])

	useEffect(() => {
		let mounted = true
		const isMounted = () => mounted
		const params = { userId, millis, subRole: subRoleParam, platforms: platformsParam }

		dispatch(showLoader(true))

		Promise.allSettled(activeConfig.fetchList.map(({ fetchKey, apiFn }) => runFetch(fetchKey, apiFn(params), isMounted))).finally(() => {
			dispatch(showLoader(false))
		})

		return () => {
			mounted = false
		}
	}, [dispatch, runFetch, userId, millis, subRoleParam, platformsParam, activeConfig])

	const listFor = useCallback(
		(card) => {
			const response = responses[card.responseKey ?? card.dataKey]
			return card.dataPath ? response?.[card.dataPath] : response
		},
		[responses]
	)

	const rowsFor = useMemo(() => (card) => normalizeRows(listFor(card), card), [listFor])

	const totalFor = (card) => {
		if (!card.showTotal) return null
		const response = listFor(card)
		if (!Array.isArray(response) || !response.length) return null
		const total = response.reduce((sum, item) => {
			const value = item?.[card.valueKey]
			return sum + (card.unitKey ? toBaseRevenue(value, item?.[card.unitKey]) : Number(value) || 0)
		}, 0)
		return card.formatTotal ? card.formatTotal(total) : total
	}

	const renderBody = (card) => {
		const rows = rowsFor(card)
		if (loadingMap[card.responseKey ?? card.dataKey]) return <StateMessage>Loading…</StateMessage>
		if (!rows.length) return <StateMessage>No data available</StateMessage>
		return <ScrollableTable columns={buildColumns(card)} data={rows} hideFooter hideHeader rowClickable={false} />
	}

	return (
		<Wrapper>
			<Section>
				{activeConfig.cards.map((card) => (
					<Card key={card.dataKey}>
						<CardHeader accent={card.accent}>
							<CardTitle>{card.title}</CardTitle>
							<HeaderEnd>
								{totalFor(card) != null && <CardTotal>{totalFor(card)}</CardTotal>}
								<EmojiEvents />
							</HeaderEnd>
						</CardHeader>
						{renderBody(card)}
					</Card>
				))}
			</Section>
		</Wrapper>
	)
}

export default Leaderboard
